"use client";

import { useState } from "react";
import Link from "next/link";
import { BlogPostMeta } from "@/lib/blog";
import { Calendar, Clock, ChevronRight, Filter } from "lucide-react";

interface Props {
  posts: BlogPostMeta[];
}

const categories = [
  "All Articles",
  "MOT & DVSA",
  "Booking Automation",
  "Garage Growth",
  "Product Updates",
];

export default function BlogListWithFilter({ posts }: Props) {
  const [activeCategory, setActiveCategory] = useState("All Articles");

  const filteredPosts = posts.filter((post) => {
    if (activeCategory === "All Articles") return true;
    return (
      post.category === activeCategory ||
      (post.tags && post.tags.includes(activeCategory))
    );
  });

  return (
    <div>
      {/* Category Filter Pills */}
      <div className="flex flex-wrap items-center gap-2 mb-10 pb-4 border-b border-slate-200">
        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1 mr-2">
          <Filter size={14} /> Filter Topic:
        </span>
        {categories.map((cat) => {
          const isActive = activeCategory === cat;
          return (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-all cursor-pointer ${
                isActive
                  ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                  : "bg-white text-slate-700 border border-slate-200 hover:border-blue-300 hover:text-blue-600"
              }`}
            >
              {cat}
            </button>
          );
        })}
      </div>

      {/* Grid of Posts */}
      {filteredPosts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border-2 border-slate-200/90 text-slate-500 text-sm">
          No articles found in <strong>{activeCategory}</strong>.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {filteredPosts.map((post) => (
            <Link
              key={post.slug}
              href={`/blog/${post.slug}`}
              className="group bg-white border-2 border-slate-200/90 hover:border-blue-300 rounded-3xl p-7 sm:p-8 shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col justify-between"
            >
              <div>
                {/* Meta Bar */}
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <div className="flex items-center gap-3 text-xs text-slate-500 font-medium">
                    <span className="flex items-center gap-1">
                      <Calendar size={13} className="text-blue-600" /> {post.date}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Clock size={13} className="text-teal-600" /> {post.readTime}
                    </span>
                  </div>

                  <span className="text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 px-3 py-0.5 rounded-full">
                    {post.category || post.tags[0] || "Garage Growth"}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 group-hover:text-blue-600 transition-colors leading-snug mb-3">
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed mb-6">
                  {post.excerpt}
                </p>
              </div>

              {/* Card Footer */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs sm:text-sm font-bold text-blue-600 group-hover:text-blue-700">
                <span className="flex items-center gap-1.5">Read Full Article</span>
                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
